import React from "react";

import { useGetFootprintingDairyPrimaryDataById, useTranslation } from "utils";

import { AlertBanner, Card, Flex, Icon, PageSpin, Tag, Typography } from "components";

interface DairyGettingStartedProps {
  primaryDataId?: string;
  /** Refrigeration records are only needed when the refrigerants step is shown (CFP dairy) */
  includeRefrigeration?: boolean;
}

const INFO_ITEMS = [
  "dairyGettingStartedForm.info.gain",
  "dairyGettingStartedForm.info.breakdown",
  "dairyGettingStartedForm.info.compare",
  "dairyGettingStartedForm.info.discover",
  "dairyGettingStartedForm.info.track",
];

const DAIRY_LIST_FILES = [
  "milk",
  "cowCull",
  "cattleMovement",
  "concentrateFeed",
  "forage",
  "agSupport",
  "nutrientManagement",
  "electricity",
  "diesel",
  "refrigeration",
  "contractor",
];

const DairyGettingStartedForm = ({ primaryDataId, includeRefrigeration = false }: DairyGettingStartedProps) => {
  const { t } = useTranslation("footprintingDairy");
  const { isLoading } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");

  if (isLoading) return <PageSpin />;

  return (
    <Flex vertical gap={16}>
      <Card border className="padding-200">
        {/* TODO: Replace with non-AI image */}
        {/* <Flex gap="1rem" vertical>
          <LazyLoadImage alt={"/images/onboarding/farm.jpg"} aria-label="farm-logo-image" src={"/images/onboarding/dairy.webp"} />
        </Flex> */}
        <Flex gap={8} vertical>
          {INFO_ITEMS.map((item) => (
            <Flex key={item} align="center" gap={8}>
              <Icon size={24} iconName="tabler/circle-check" className="input-label-info-icon margin-left-0" />
              <Typography variant="paragraph-m">{t(item)}</Typography>
            </Flex>
          ))}
        </Flex>
      </Card>

      <Card border dataTestId="footprinting-dairy-getting-started-form" className="padding-200">
        <Flex vertical gap={24}>
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center">
              <Typography variant="h3">{t("dairyGettingStartedForm.documents")}</Typography>
              <Tag colour="blue" size="large" text={t("dairyGettingStartedForm.tag")} className="center" />
            </Flex>
            <Typography variant="paragraph-m" color="grey-600" className="margin-0">
              {t("dairyGettingStartedForm.documentsDescription")}
            </Typography>
          </Flex>

          <Flex vertical gap={4}>
            {DAIRY_LIST_FILES.filter((file) => includeRefrigeration || file !== "refrigeration").map((file) => (
              <div key={file}>
                <Typography variant="paragraph-m" className="margin-right-50">
                  •
                </Typography>
                <Typography variant="paragraph-m">{t(`dairyGettingStartedForm.dairyListFiles.${file}`)}</Typography>
              </div>
            ))}
          </Flex>

          <AlertBanner
            headingSize="h6"
            gap={12}
            title={t("dairyGettingStartedForm.alertBanner.title")}
            description={t("dairyGettingStartedForm.alertBanner.description")}
            iconName="tabler/bulb"
            iconSize={24}
            type="recommended"
            iconAlign="start"
          />
        </Flex>
      </Card>

      <AlertBanner
        headingSize="h6"
        gap={12}
        title={t("dairyGettingStartedForm.infoBanner.title")}
        description={t("dairyGettingStartedForm.infoBanner.description")}
        iconName="tabler/info-circle"
        iconSize={24}
        type="info"
        iconAlign="start"
      />
    </Flex>
  );
};

DairyGettingStartedForm.Key = "dairy.gettingStarted";
export { DairyGettingStartedForm };
